import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { subDays, format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Box, Container, Typography, Paper, Grid, Tabs, Tab, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Alert, TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BarChartIcon from '@mui/icons-material/BarChart';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

// ─── helpers ────────────────────────────────────────────────────────────────

function npsColor(score) {
  if (score === null || score === undefined) return '#9e9e9e';
  if (score >= 50) return '#2e7d32';
  if (score >= 30) return '#558b2f';
  if (score >= 0) return '#f57c00';
  return '#c62828';
}

function npsLabel(score) {
  if (score === null || score === undefined) return '—';
  if (score >= 70) return 'Excellent';
  if (score >= 50) return 'Good';
  if (score >= 30) return 'Decent';
  if (score >= 0) return 'Needs Improvement';
  return 'Poor';
}

function scoreColor(val) {
  if (!val) return '#9e9e9e';
  if (val >= 4.5) return '#2e7d32';
  if (val >= 4.0) return '#558b2f';
  if (val >= 3.5) return '#f57c00';
  return '#c62828';
}

function fmtDate(val) {
  if (!val) return '—';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d, yyyy'); }
  catch { return val; }
}

function fmtWeek(val) {
  if (!val) return '';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d'); }
  catch { return val; }
}

function buildParams(filters) {
  const p = new URLSearchParams();
  if (filters.startDate) p.set('startDate', format(filters.startDate, 'yyyy-MM-dd'));
  if (filters.endDate) p.set('endDate', format(filters.endDate, 'yyyy-MM-dd'));
  if (filters.store) p.set('store', filters.store);
  if (filters.region) p.set('region', filters.region);
  if (filters.storeType) p.set('storeType', filters.storeType);
  if (filters.districtOperator) p.set('districtOperator', filters.districtOperator);
  if (filters.orderChannel) p.set('orderChannel', filters.orderChannel);
  if (filters.whereConsumed) p.set('whereConsumed', filters.whereConsumed);
  if (filters.deviceType) p.set('deviceType', filters.deviceType);
  return p;
}

const RATING_FIELDS = [
  { key: 'OVERALL_EXPERIENCE', label: 'Overall Experience' },
  { key: 'QUALITY_OF_FOOD', label: 'Quality of Food' },
  { key: 'VALUE_FOR_PRICE', label: 'Value for Price' },
  { key: 'SPEED_OF_SERVICE', label: 'Speed of Service' },
  { key: 'CLEANLINESS', label: 'Cleanliness' },
  { key: 'FRIENDLINESS', label: 'Friendliness' },
];

const FUNNEL_STEPS = [
  { key: 'TOTAL_SENT', label: 'Sent' },
  { key: 'STARTED', label: 'Started' },
  { key: 'COMPLETED', label: 'Completed' },
];

// ─── sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color, small }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant={small ? 'h5' : 'h4'} fontWeight={700} sx={{ color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function RatingBar({ label, value }) {
  const pct = value ? (value / 5) * 100 : 0;
  const color = scoreColor(value);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ color }}>{value ? value.toFixed(2) : '—'} / 5</Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 4, transition: 'width .4s' }} />
      </Box>
    </Box>
  );
}

function FunnelBar({ label, value, max, color }) {
  const pct = max ? (value / max) * 100 : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>{(value || 0).toLocaleString()}</Typography>
      </Box>
      <Box sx={{ height: 20, borderRadius: 2, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 2, transition: 'width .4s' }} />
      </Box>
    </Box>
  );
}

// ─── filter bar ──────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters, dimensions, onApply, loading }) {
  const selects = [
    { key: 'region', label: 'Region', options: dimensions.regions || [] },
    { key: 'store', label: 'Store', options: dimensions.stores || [] },
    { key: 'storeType', label: 'Store Type', options: dimensions.storeTypes || [] },
    { key: 'districtOperator', label: 'District Operator', options: dimensions.districts || [] },
    { key: 'orderChannel', label: 'Order Channel', options: dimensions.orderChannels || [] },
    { key: 'whereConsumed', label: 'Where Consumed', options: dimensions.whereConsumed || [] },
    { key: 'deviceType', label: 'Device', options: ['MOBILE', 'DESKTOP'] },
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <DatePicker
          selected={filters.startDate}
          onChange={d => setFilters(f => ({ ...f, startDate: d }))}
          selectsStart
          startDate={filters.startDate}
          endDate={filters.endDate}
          dateFormat="MMM d, yyyy"
          customInput={<TextField size="small" label="Start Date" sx={{ width: 150 }} autoComplete="off" />}
        />
        <DatePicker
          selected={filters.endDate}
          onChange={d => setFilters(f => ({ ...f, endDate: d }))}
          selectsEnd
          startDate={filters.startDate}
          endDate={filters.endDate}
          minDate={filters.startDate}
          dateFormat="MMM d, yyyy"
          customInput={<TextField size="small" label="End Date" sx={{ width: 150 }} autoComplete="off" />}
        />
        {selects.map(({ key, label, options }) => (
          <FormControl key={key} size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={filters[key]}
              label={label}
              onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <Button variant="contained" size="small" onClick={onApply} disabled={loading} startIcon={<RefreshIcon />}>
          Apply
        </Button>
      </Box>
    </Paper>
  );
}

// ─── tabs ────────────────────────────────────────────────────────────────────

function ScorecardTab({ data, loading }) {
  const [byStorePage, setByStorePage] = useState(0);
  const [byStorePerPage, setByStorePerPage] = useState(25);
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { summary, trend, byStore, byDevice } = data;
  const byStorePaged = (byStore || []).slice(byStorePage * byStorePerPage, (byStorePage + 1) * byStorePerPage);
  const total = summary.TOTAL_RESPONSES || 0;
  const promoterPct = total ? ((summary.PROMOTERS / total) * 100).toFixed(1) : '—';
  const passivePct = total ? ((summary.PASSIVES / total) * 100).toFixed(1) : '—';
  const detractorPct = total ? ((summary.DETRACTORS / total) * 100).toFixed(1) : '—';
  const nps = summary.NPS_SCORE;

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="NPS Score"
            value={nps !== null && nps !== undefined ? nps : '—'}
            sub={npsLabel(nps)}
            color={npsColor(nps)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Total Responses" value={(total || 0).toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Promoters" value={summary.PROMOTERS || 0} sub={`${promoterPct}%`} color="#2e7d32" small />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Passives" value={summary.PASSIVES || 0} sub={`${passivePct}%`} color="#f57c00" small />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Detractors" value={summary.DETRACTORS || 0} sub={`${detractorPct}%`} color="#c62828" small />
        </Grid>
      </Grid>

      {trend && trend.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" mb={2}>Weekly NPS Trend</Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="WEEK" tickFormatter={fmtWeek} tick={{ fontSize: 11 }} />
              <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(v) => [v, 'NPS Score']}
                labelFormatter={(l) => fmtWeek(l)}
              />
              <Line type="monotone" dataKey="NPS_SCORE" stroke="#163350" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {byDevice && byDevice.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" mb={2}>NPS by Device Type</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell align="right">Promoters</TableCell>
                <TableCell align="right">Passives</TableCell>
                <TableCell align="right">Detractors</TableCell>
                <TableCell align="right">NPS Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byDevice.map((row, i) => {
                const total = row.RESPONSES || 0;
                return (
                  <TableRow key={i} hover>
                    <TableCell>{row.DEVICE_TYPE || '—'}</TableCell>
                    <TableCell align="right">{(total).toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: '#2e7d32' }}>
                      {total ? `${((row.PROMOTERS / total) * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#f57c00' }}>
                      {total ? `${((row.PASSIVES / total) * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#c62828' }}>
                      {total ? `${((row.DETRACTORS / total) * 100).toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ color: npsColor(row.NPS_SCORE) }}>
                        {row.NPS_SCORE}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {byStore && byStore.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={2}>NPS by Store <Typography component="span" variant="caption" color="text.secondary">(min. 5 responses)</Typography></Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Store</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Responses</TableCell>
                  <TableCell align="right">NPS Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byStorePaged.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{row.STORE_NAME}</TableCell>
                    <TableCell>{row.STORE_REGION}</TableCell>
                    <TableCell align="right">{(row.RESPONSES || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ color: npsColor(row.NPS_SCORE) }}>
                        {row.NPS_SCORE}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={byStore.length}
            page={byStorePage}
            onPageChange={(_, p) => setByStorePage(p)}
            rowsPerPage={byStorePerPage}
            onRowsPerPageChange={(e) => { setByStorePerPage(parseInt(e.target.value, 10)); setByStorePage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Paper>
      )}
    </Box>
  );
}

function RatingsTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const chartData = RATING_FIELDS.map(f => ({
    label: f.label,
    value: data[f.key] || 0,
  }));

  const byDevice = data.byDevice || [];
  const mobile = byDevice.find(d => d.DEVICE_TYPE === 'MOBILE') || {};
  const desktop = byDevice.find(d => d.DEVICE_TYPE === 'DESKTOP') || {};
  const deviceChartData = RATING_FIELDS.map(f => ({
    label: f.label,
    MOBILE: mobile[f.key] || 0,
    DESKTOP: desktop[f.key] || 0,
  }));

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        {RATING_FIELDS.map(({ key, label }) => (
          <Grid item xs={6} sm={4} md={2} key={key}>
            <KpiCard
              label={label}
              value={data[key] ? data[key].toFixed(2) : '—'}
              sub="out of 5"
              color={scoreColor(data[key])}
              small
            />
          </Grid>
        ))}
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Order Accuracy"
            value={data.ORDER_ACCURACY_PCT !== undefined ? `${data.ORDER_ACCURACY_PCT}%` : '—'}
            color={data.ORDER_ACCURACY_PCT >= 95 ? '#2e7d32' : data.ORDER_ACCURACY_PCT >= 85 ? '#f57c00' : '#c62828'}
            small
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" mb={2}>Average Rating by Category</Typography>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 120, right: 40, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
            <YAxis dataKey="label" type="category" tick={{ fontSize: 12 }} width={120} />
            <Tooltip formatter={(v) => [v.toFixed(2), 'Avg Score']} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={scoreColor(entry.value)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {byDevice.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={2}>Ratings by Device Type</Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={deviceChartData} layout="vertical" margin={{ left: 120, right: 40, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
              <YAxis dataKey="label" type="category" tick={{ fontSize: 12 }} width={120} />
              <Tooltip formatter={(v) => [v ? v.toFixed(2) : '—', '']} />
              <Legend />
              <Bar dataKey="MOBILE" fill="#5BADD6" radius={[0, 4, 4, 0]} maxBarSize={16} />
              <Bar dataKey="DESKTOP" fill="#163350" radius={[0, 4, 4, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
}

function bucketBarColor(rating, scale) {
  if (scale === '0-10') {
    if (rating >= 9) return '#2e7d32';
    if (rating >= 7) return '#f57c00';
    return '#c62828';
  }
  if (rating >= 4) return '#2e7d32';
  if (rating === 3) return '#f57c00';
  return '#c62828';
}

function RatingBreakdownTab({ data, loading, onExport, exporting }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;
  const metrics = data.metrics || [];
  if (!metrics.length) {
    return <Alert severity="info">No rating data available for the selected filters.</Alert>;
  }

  const oneToFive = metrics.filter(m => m.scale === '1-5');
  const npsMetric = metrics.find(m => m.scale === '0-10');
  const maxRatings = [1,2,3,4,5];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
          onClick={onExport}
          disabled={exporting}
        >
          Export XLSX
        </Button>
      </Box>

      <Grid container spacing={2} mb={2}>
        {oneToFive.map(m => (
          <Grid item xs={12} sm={6} md={4} key={m.key}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                <Typography variant="subtitle2">{m.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Avg {m.avg != null ? m.avg.toFixed(2) : '—'} · n={m.total.toLocaleString()}
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={m.buckets} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="rating" tick={{ fontSize: 11 }} type="category" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v.toLocaleString(), 'Responses']} labelFormatter={(l) => `${l} / 5`} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {m.buckets.map((b, i) => (
                      <Cell key={i} fill={bucketBarColor(b.rating, m.scale)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {npsMetric && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="subtitle2">NPS Score (0-10)</Typography>
            <Typography variant="caption" color="text.secondary">
              Avg {npsMetric.avg != null ? npsMetric.avg.toFixed(2) : '—'} · n={npsMetric.total.toLocaleString()}
            </Typography>
          </Box>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={npsMetric.buckets} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="rating" tick={{ fontSize: 11 }} type="category" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [v.toLocaleString(), 'Responses']} labelFormatter={(l) => `Score ${l}`} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {npsMetric.buckets.map((b, i) => (
                  <Cell key={i} fill={bucketBarColor(b.rating, npsMetric.scale)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" mb={1}>Summary (1-5 ratings)</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Metric</TableCell>
                {maxRatings.map(r => <TableCell key={r} align="right">{r}</TableCell>)}
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Avg</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oneToFive.map(m => {
                const byRating = new Map(m.buckets.map(b => [b.rating, b.count]));
                return (
                  <TableRow key={m.key} hover>
                    <TableCell>{m.label}</TableCell>
                    {maxRatings.map(r => (
                      <TableCell key={r} align="right">{(byRating.get(r) || 0).toLocaleString()}</TableCell>
                    ))}
                    <TableCell align="right">{m.total.toLocaleString()}</TableCell>
                    <TableCell align="right">{m.avg != null ? m.avg.toFixed(2) : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function FunnelTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { funnel, abandonmentByPage } = data;
  const sent = funnel.TOTAL_SENT || 0;

  const funnelColors = { Sent: '#5BADD6', Started: '#163350', Completed: '#2e7d32' };

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Invitations Sent" value={(sent || 0).toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Started" value={(funnel.STARTED || 0).toLocaleString()}
            sub={sent ? `${((funnel.STARTED / sent) * 100).toFixed(1)}% of sent` : ''}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Completed" value={(funnel.COMPLETED || 0).toLocaleString()}
            sub={sent ? `${((funnel.COMPLETED / sent) * 100).toFixed(1)}% of sent` : ''}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <KpiCard label="Response Rate" value={funnel.RESPONSE_RATE_PCT !== undefined ? `${funnel.RESPONSE_RATE_PCT}%` : '—'}
            color={funnel.RESPONSE_RATE_PCT >= 20 ? '#2e7d32' : funnel.RESPONSE_RATE_PCT >= 10 ? '#f57c00' : '#c62828'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={2}>Survey Funnel</Typography>
            {FUNNEL_STEPS.map(({ key, label }) => (
              <FunnelBar key={key} label={label} value={funnel[key]} max={sent} color={funnelColors[label]} />
            ))}
          </Paper>
        </Grid>

        {abandonmentByPage && abandonmentByPage.length > 0 && (
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" mb={2}>Abandonment by Page</Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={abandonmentByPage} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="PAGE_NAME" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v, 'Abandoned']} />
                  <Bar dataKey="ABANDONED_COUNT" fill="#c62828" radius={[4, 4, 0, 0]} maxBarSize={60} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function DetractorsTab({ data, loading, page, pageSize, onPageChange }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { rows, total } = data;

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">NPS</TableCell>
              <TableCell align="center">Overall</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Feedback</TableCell>
              <TableCell align="center">Alert</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>No detractor responses for the selected filters.</Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.SURVEY_COMPLETION_DATE)}</TableCell>
                <TableCell>{row.STORE_NAME}</TableCell>
                <TableCell>{row.STORE_REGION}</TableCell>
                <TableCell>{row.CUSTOMER_EMAIL}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.Q2_NPS_SCORE}
                    size="small"
                    sx={{ bgcolor: npsColor(row.Q2_NPS_SCORE <= 6 ? -1 : row.Q2_NPS_SCORE), color: '#fff', fontWeight: 700, minWidth: 36 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.Q105_OVERALL_EXPERIENCE || '—'}
                    size="small"
                    sx={{ bgcolor: row.Q105_OVERALL_EXPERIENCE <= 2 ? '#c62828' : '#e0e0e0', color: row.Q105_OVERALL_EXPERIENCE <= 2 ? '#fff' : 'text.primary', minWidth: 36 }}
                  />
                </TableCell>
                <TableCell>{row.ORDER_SOURCE}</TableCell>
                <TableCell>{row.DEVICE_TYPE || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {row.Q6_ADDITIONAL_FEEDBACK || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {row.ALERT_SENT
                    ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                    : <NotificationsActiveIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, p) => onPageChange(p)}
        onRowsPerPageChange={() => {}}
      />
    </Paper>
  );
}

// Shared table for Promoter and Passive comment views. Same shape as
// DetractorsTab minus the Alert column — there's no alerting on positive
// feedback. Row rendering is identical except the Overall chip stays
// neutral since there are no "low overall" callouts for positive groups.
function CommentsTab({ data, loading, page, pageSize, onPageChange, emptyLabel }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { rows, total } = data;

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">NPS</TableCell>
              <TableCell align="center">Overall</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Feedback</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>{emptyLabel}</Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.SURVEY_COMPLETION_DATE)}</TableCell>
                <TableCell>{row.STORE_NAME}</TableCell>
                <TableCell>{row.STORE_REGION}</TableCell>
                <TableCell>{row.CUSTOMER_EMAIL}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.Q2_NPS_SCORE}
                    size="small"
                    sx={{ bgcolor: npsColor(row.Q2_NPS_SCORE), color: '#fff', fontWeight: 700, minWidth: 36 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={row.Q105_OVERALL_EXPERIENCE || '—'}
                    size="small"
                    sx={{ bgcolor: '#e0e0e0', color: 'text.primary', minWidth: 36 }}
                  />
                </TableCell>
                <TableCell>{row.ORDER_SOURCE}</TableCell>
                <TableCell>{row.DEVICE_TYPE || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {row.Q6_ADDITIONAL_FEEDBACK || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, p) => onPageChange(p)}
        onRowsPerPageChange={() => {}}
      />
    </Paper>
  );
}

function DeviceTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { summary, trend, ratings } = data;
  const mobile = summary.find(d => d.DEVICE_TYPE === 'MOBILE') || {};
  const desktop = summary.find(d => d.DEVICE_TYPE === 'DESKTOP') || {};

  // Pivot trend rows into wide format for recharts: [{WEEK, MOBILE, DESKTOP}]
  const trendWide = trend.reduce((acc, row) => {
    const existing = acc.find(r => r.WEEK === row.WEEK);
    if (existing) {
      existing[row.DEVICE_TYPE] = row.NPS_SCORE;
    } else {
      acc.push({ WEEK: row.WEEK, [row.DEVICE_TYPE]: row.NPS_SCORE });
    }
    return acc;
  }, []).sort((a, b) => (a.WEEK < b.WEEK ? -1 : 1));

  const mobileRatings = ratings.find(d => d.DEVICE_TYPE === 'MOBILE') || {};
  const desktopRatings = ratings.find(d => d.DEVICE_TYPE === 'DESKTOP') || {};
  const ratingsChartData = RATING_FIELDS.map(f => ({
    label: f.label,
    MOBILE: mobileRatings[f.key] || 0,
    DESKTOP: desktopRatings[f.key] || 0,
  }));

  function DeviceSummaryCard({ device, row }) {
    const total = row.RESPONSES || 0;
    return (
      <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
        <Typography variant="subtitle2" mb={1.5} sx={{ color: device === 'MOBILE' ? '#5BADD6' : '#163350', fontWeight: 700 }}>
          {device}
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <KpiCard label="NPS Score" value={row.NPS_SCORE ?? '—'} color={npsColor(row.NPS_SCORE)} sub={npsLabel(row.NPS_SCORE)} />
          </Grid>
          <Grid item xs={6}>
            <KpiCard label="Responses" value={(total).toLocaleString()} />
          </Grid>
          <Grid item xs={4}>
            <KpiCard label="Promoters" value={total ? `${((row.PROMOTERS / total) * 100).toFixed(1)}%` : '—'} color="#2e7d32" small />
          </Grid>
          <Grid item xs={4}>
            <KpiCard label="Passives" value={total ? `${((row.PASSIVES / total) * 100).toFixed(1)}%` : '—'} color="#f57c00" small />
          </Grid>
          <Grid item xs={4}>
            <KpiCard label="Detractors" value={total ? `${((row.DETRACTORS / total) * 100).toFixed(1)}%` : '—'} color="#c62828" small />
          </Grid>
        </Grid>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <DeviceSummaryCard device="MOBILE" row={mobile} />
        <DeviceSummaryCard device="DESKTOP" row={desktop} />
      </Box>

      {trendWide.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" mb={2}>Weekly NPS Trend by Device</Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendWide} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="WEEK" tickFormatter={fmtWeek} tick={{ fontSize: 11 }} />
              <YAxis domain={[-100, 100]} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={fmtWeek} formatter={(v, name) => [v, name]} />
              <Legend />
              <Line type="monotone" dataKey="MOBILE" stroke="#5BADD6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="DESKTOP" stroke="#163350" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {ratingsChartData.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={2}>Average Ratings by Device</Typography>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ratingsChartData} layout="vertical" margin={{ left: 120, right: 40, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
              <YAxis dataKey="label" type="category" tick={{ fontSize: 12 }} width={120} />
              <Tooltip formatter={(v) => [v ? v.toFixed(2) : '—', '']} />
              <Legend />
              <Bar dataKey="MOBILE" fill="#5BADD6" radius={[0, 4, 4, 0]} maxBarSize={16} />
              <Bar dataKey="DESKTOP" fill="#163350" radius={[0, 4, 4, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

const defaultFilters = () => ({
  startDate: subDays(new Date(), 30),
  endDate: new Date(),
  store: '',
  region: '',
  storeType: '',
  districtOperator: '',
  orderChannel: '',
  whereConsumed: '',
  deviceType: '',
});

export default function NPSDashboard() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState(defaultFilters());
  const [refreshKey, setRefreshKey] = useState(0);
  const [dimensions, setDimensions] = useState({});

  const [scorecard, setScorecard] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [detractors, setDetractors] = useState(null);
  const [detractorPage, setDetractorPage] = useState(0);
  const [promoters, setPromoters] = useState(null);
  const [promoterPage, setPromoterPage] = useState(0);
  const [passives, setPassives] = useState(null);
  const [passivePage, setPassivePage] = useState(0);
  const [device, setDevice] = useState(null);

  const [loading, setLoading] = useState({ scorecard: false, ratings: false, breakdown: false, funnel: false, detractors: false, promoters: false, passives: false, device: false, dimensions: false });
  const [error, setError] = useState(null);
  const [exportingEmails, setExportingEmails] = useState(false);
  const [exportingBreakdown, setExportingBreakdown] = useState(false);

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  async function handleExportEmails() {
    const token = await getToken();
    if (!token) return;
    setExportingEmails(true);
    try {
      const { data } = await axios.get('/hd-api/nps/export-emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const csv = ['Date,Email', ...data.map(r => `${r.DATE},${r.EMAIL}`)].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nps-emails.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export emails');
    } finally {
      setExportingEmails(false);
    }
  }

  // Load filter dimension options once
  useEffect(() => {
    (async () => {
      setLoad('dimensions', true);
      try {
        const token = await getToken();
        if (!token) return;
        const { data } = await axios.get('/hd-api/nps/dimensions', { headers: { Authorization: `Bearer ${token}` } });
        setDimensions(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoad('dimensions', false);
      }
    })();
  }, [getToken]);

  // Auto-fetch when tab, filters, or refreshKey change
  useEffect(() => {
    setDetractorPage(0);
    setPromoterPage(0);
    setPassivePage(0);
    loadTabData(tab, filters, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters, refreshKey]);

  // Reload paginated tabs when their page changes
  useEffect(() => {
    if (tab === 4) loadDetractors(filters, detractorPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detractorPage]);

  useEffect(() => {
    if (tab === 5) loadPromoters(filters, promoterPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoterPage]);

  useEffect(() => {
    if (tab === 6) loadPassives(filters, passivePage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passivePage]);

  async function loadTabData(activeTab, f, p = 0) {
    setError(null);
    if (activeTab === 0) await loadScorecard(f);
    if (activeTab === 1) await loadRatings(f);
    if (activeTab === 2) await loadBreakdown(f);
    if (activeTab === 3) await loadFunnel(f);
    if (activeTab === 4) await loadDetractors(f, p);
    if (activeTab === 5) await loadPromoters(f, p);
    if (activeTab === 6) await loadPassives(f, p);
    if (activeTab === 7) await loadDevice(f);
  }

  async function loadScorecard(f) {
    setLoad('scorecard', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps/scorecard?${buildParams(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      setScorecard(data);
    } catch (e) {
      setError('Failed to load scorecard data.');
    } finally {
      setLoad('scorecard', false);
    }
  }

  async function loadRatings(f) {
    setLoad('ratings', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps/ratings?${buildParams(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      setRatings(data);
    } catch (e) {
      setError('Failed to load ratings data.');
    } finally {
      setLoad('ratings', false);
    }
  }

  async function loadBreakdown(f) {
    setLoad('breakdown', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps/rating-distribution?${buildParams(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      setBreakdown(data);
    } catch (e) {
      setError('Failed to load rating breakdown data.');
    } finally {
      setLoad('breakdown', false);
    }
  }

  function handleExportBreakdown() {
    if (!breakdown?.metrics?.length) return;
    setExportingBreakdown(true);
    try {
      const oneToFive = breakdown.metrics.filter(m => m.scale === '1-5');
      const npsMetric = breakdown.metrics.find(m => m.scale === '0-10');

      const summaryRows = oneToFive.map(m => {
        const byRating = new Map(m.buckets.map(b => [b.rating, b.count]));
        return {
          Metric: m.label,
          'Rating 1': byRating.get(1) || 0,
          'Rating 2': byRating.get(2) || 0,
          'Rating 3': byRating.get(3) || 0,
          'Rating 4': byRating.get(4) || 0,
          'Rating 5': byRating.get(5) || 0,
          Total: m.total,
          Average: m.avg,
        };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), '1-5 Ratings');

      if (npsMetric) {
        const npsRows = npsMetric.buckets.map(b => ({ Score: b.rating, Responses: b.count }));
        npsRows.push({ Score: 'Total', Responses: npsMetric.total });
        npsRows.push({ Score: 'Average', Responses: npsMetric.avg });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(npsRows), 'NPS Score (0-10)');
      }

      const from = filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : 'all';
      const to = filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : 'all';
      XLSX.writeFile(wb, `nps-rating-breakdown_${from}_to_${to}.xlsx`);
    } catch (e) {
      setError('Failed to export rating breakdown.');
    } finally {
      setExportingBreakdown(false);
    }
  }

  async function loadFunnel(f) {
    setLoad('funnel', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps/funnel?${buildParams(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      setFunnel(data);
    } catch (e) {
      setError('Failed to load funnel data.');
    } finally {
      setLoad('funnel', false);
    }
  }

  async function loadDetractors(f, p = 0) {
    setLoad('detractors', true);
    try {
      const token = await getToken();
      if (!token) return;
      const params = buildParams(f);
      params.set('page', p + 1);
      params.set('pageSize', 50);
      const { data } = await axios.get(`/hd-api/nps/detractors?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setDetractors(data);
    } catch (e) {
      setError('Failed to load detractor data.');
    } finally {
      setLoad('detractors', false);
    }
  }

  async function loadPromoters(f, p = 0) {
    setLoad('promoters', true);
    try {
      const token = await getToken();
      if (!token) return;
      const params = buildParams(f);
      params.set('page', p + 1);
      params.set('pageSize', 50);
      const { data } = await axios.get(`/hd-api/nps/promoters?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setPromoters(data);
    } catch (e) {
      setError('Failed to load promoter comments.');
    } finally {
      setLoad('promoters', false);
    }
  }

  async function loadPassives(f, p = 0) {
    setLoad('passives', true);
    try {
      const token = await getToken();
      if (!token) return;
      const params = buildParams(f);
      params.set('page', p + 1);
      params.set('pageSize', 50);
      const { data } = await axios.get(`/hd-api/nps/passives?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setPassives(data);
    } catch (e) {
      setError('Failed to load passive comments.');
    } finally {
      setLoad('passives', false);
    }
  }

  async function loadDevice(f) {
    setLoad('device', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps/device?${buildParams(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      setDevice(data);
    } catch (e) {
      setError('Failed to load device breakdown data.');
    } finally {
      setLoad('device', false);
    }
  }

  function handleApply() {
    setRefreshKey(k => k + 1);
  }

  function handleTabChange(_, v) {
    setTab(v);
  }

  function handleDetractorPage(p) {
    setDetractorPage(p);
  }

  const tabLoading = [loading.scorecard, loading.ratings, loading.breakdown, loading.funnel, loading.detractors, loading.promoters, loading.passives, loading.device][tab];

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth='xl'>
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BarChartIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  NPS Reporting Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Guest experience survey data from Snowflake
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="outlined"
                size="small"
                startIcon={exportingEmails ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={handleExportEmails}
                disabled={exportingEmails}
              >
                Export Emails
              </Button>
            </Box>
          </Paper>

          <FilterBar
            filters={filters}
            setFilters={setFilters}
            dimensions={dimensions}
            onApply={handleApply}
            loading={tabLoading}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab label="NPS Scorecard" />
              <Tab label="Ratings Summary" />
              <Tab label="Rating Breakdown" />
              <Tab label="Survey Funnel" />
              <Tab label="Detractors / Alerts" />
              <Tab label="Promoter Comments" />
              <Tab label="Passive Comments" />
              <Tab label="Device Breakdown" />
            </Tabs>
            <Box sx={{ p: 2 }}>
              {tab === 0 && <ScorecardTab data={scorecard} loading={loading.scorecard} />}
              {tab === 1 && <RatingsTab data={ratings} loading={loading.ratings} />}
              {tab === 2 && (
                <RatingBreakdownTab
                  data={breakdown}
                  loading={loading.breakdown}
                  onExport={handleExportBreakdown}
                  exporting={exportingBreakdown}
                />
              )}
              {tab === 3 && <FunnelTab data={funnel} loading={loading.funnel} />}
              {tab === 4 && (
                <DetractorsTab
                  data={detractors}
                  loading={loading.detractors}
                  page={detractorPage}
                  pageSize={50}
                  onPageChange={handleDetractorPage}
                />
              )}
              {tab === 5 && (
                <CommentsTab
                  data={promoters}
                  loading={loading.promoters}
                  page={promoterPage}
                  pageSize={50}
                  onPageChange={setPromoterPage}
                  emptyLabel="No promoter comments for the selected filters."
                />
              )}
              {tab === 6 && (
                <CommentsTab
                  data={passives}
                  loading={loading.passives}
                  page={passivePage}
                  pageSize={50}
                  onPageChange={setPassivePage}
                  emptyLabel="No passive comments for the selected filters."
                />
              )}
              {tab === 7 && <DeviceTab data={device} loading={loading.device} />}
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}
